/**
 * CirurgiasTab - Aba de Cirurgias a Faturar (Sistema Completo)
 * Usando MantineReactTable para filtros, ordenação e paginação
 *
 * Features:
 * - Toolbar com Export/Import/Add buttons
 * - Tabela editável com +/- para quantidade
 * - Integração com API via cirurgiasFaturarService
 * - Modais de Import Excel e Add Cirurgia
 */
import React, { useCallback, useMemo } from 'react';
import { Download, Plus, Trash2, RefreshCw, Stethoscope, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import PermissionGate from '../PermissionGate';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { ActionIcon, Badge } from '@mantine/core';
import cirurgiasFaturarService, {
  type ItemCirurgiaFaturar,
} from '../../services/cirurgiasFaturarService';
import { ImportExcelCirurgiasModal } from './ImportExcelCirurgiasModal';
import { AddCirurgiaModal } from './AddCirurgiaModal';
import { CirurgiasInfoModal } from './CirurgiasInfoModal';
import { ValidadeLegend, getValidadeStyle } from './ValidadeLegend';
import { CirurgiasQuantityCell } from './CirurgiasQuantityCell';
import type { Cliente } from '../../services/clienteService';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';
import { useCirurgiasTab } from '../../hooks/useCirurgiasTab';

interface CirurgiasTabProps {
  jobId: number;
  jobClientes: Cliente[];
  isReadOnly: boolean;
  isEditMode: boolean;
  globalClienteFilter?: (string | number)[];
}

// Epic 49: Helper function to calculate days until expiration
function calcularDiasParaVencer(dataValidade: string | null): number | null {
  if (!dataValidade) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const validade = new Date(dataValidade);
  validade.setHours(0, 0, 0, 0);
  const diffTime = validade.getTime() - hoje.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export const CirurgiasTab: React.FC<CirurgiasTabProps> = ({
  jobId,
  jobClientes,
  isReadOnly,
  isEditMode,
  globalClienteFilter,
}) => {
  const {
    loading,
    error,
    setError,
    downloading,
    hideLotsAndValidity,
    setHideLotsAndValidity,
    deletingId,
    addingItem,
    showImportModal,
    setShowImportModal,
    showAddModal,
    setShowAddModal,
    showInfoModal,
    setShowInfoModal,
    selectedFile,
    setSelectedFile,
    uploading,
    uploadResult,
    selectedImportClient,
    setSelectedImportClient,
    selectedRegime,
    setSelectedRegime,
    importMode,
    setImportMode,
    pastedText,
    setPastedText,
    pastedRows,
    filteredCirurgias,
    cirurgias,
    loadCirurgias,
    handleDownloadExcel,
    handleUploadExcel,
    closeImportModal,
    handleAddCirurgia,
    handleUpdateQuantity,
    handleDeleteItem,
  } = useCirurgiasTab({ jobId, jobClientes, globalClienteFilter });

  const columns = useMemo<MRT_ColumnDef<ItemCirurgiaFaturar>[]>(
    () => {
      const baseColumns: MRT_ColumnDef<ItemCirurgiaFaturar>[] = [
        {
          accessorKey: 'cliente_nome_fantasia',
          header: 'Cliente',
          size: 84,
          Cell: ({ cell }) => (
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
              {cell.getValue<string>() || '-'}
            </span>
          ),
        },
        {
          accessorKey: 'com_regime',
          header: 'Regime',
          size: 56,
          filterVariant: 'select',
          mantineFilterSelectProps: {
            data: [
              { value: 'true', label: 'Sim' },
              { value: 'false', label: 'Não' },
            ],
          },
          Cell: ({ cell }) => (
            cell.getValue<boolean>() ? (
              <Badge color="green" variant="light" size="xs">Sim</Badge>
            ) : (
              <Badge color="gray" variant="light" size="xs">Não</Badge>
            )
          ),
        },
        {
          accessorKey: 'codigo_produto',
          header: 'Produto',
          size: 70,
          Cell: ({ cell }) => (
            <span className="font-semibold text-green-700 dark:text-green-400 text-center block">
              {cell.getValue<string>()}
            </span>
          ),
        },
        {
          accessorKey: 'descricao_produto',
          header: 'Descrição',
          size: 140,
          Cell: ({ cell }) => (
            <span className="text-gray-900 dark:text-gray-100">
              {cell.getValue<string>() || '-'}
            </span>
          ),
        },
      ];

      if (!hideLotsAndValidity) {
        baseColumns.push({
          accessorKey: 'lote',
          header: 'Lote',
          size: 70,
          Cell: ({ cell }) => (
            <span className="font-mono text-gray-900 dark:text-gray-100">
              {cell.getValue<string>()}
            </span>
          ),
        });
        baseColumns.push({
          accessorKey: 'data_validade',
          header: 'Validade',
          size: 67,
          Cell: ({ cell, row }) => {
            const val = cell.getValue<string | null>();
            if (!val) return <span className="text-gray-400">-</span>;
            const diasParaVencer = calcularDiasParaVencer(val);
            const clienteNome = row.original.cliente_nome_fantasia;
            const cliente = jobClientes.find(c =>
              c.nome_fantasia === clienteNome || c.razao_social === clienteNome
            );
            const nivel1 = cliente?.sinalizar_vencimento_1 ?? 90;
            const nivel2 = cliente?.sinalizar_vencimento_2 ?? 120;
            const nivel3 = cliente?.sinalizar_vencimento_3 ?? 180;
            const { textColor, showIcon } = getValidadeStyle(diasParaVencer, nivel1, nivel2, nivel3);
            const [year, month, day] = val.split('-');
            return (
              <span className={`flex items-center gap-1 font-mono ${textColor}`}>
                {day}/{month}/{year}
                {showIcon && <AlertTriangle className="w-3 h-3" />}
              </span>
            );
          },
        });
      }

      baseColumns.push(
        {
          accessorKey: 'data_hora',
          header: 'Data/Hora',
          size: 91,
          Cell: ({ cell }) => (
            <span className="text-gray-900 dark:text-gray-100">
              {cirurgiasFaturarService.formatDataHora(cell.getValue<string>())}
            </span>
          ),
        },
        { accessorKey: 'paciente', header: 'Paciente', size: 105 },
        { accessorKey: 'medico', header: 'Médico', size: 105 },
        { accessorKey: 'convenio', header: 'Convênio', size: 84 },
        {
          accessorKey: 'quantidade',
          header: 'Qtde',
          size: 84,
          enableColumnFilter: false,
          mantineTableBodyCellProps: {
            style: { textAlign: 'center', justifyContent: 'center' }
          },
          Cell: ({ row }) => (
            <CirurgiasQuantityCell
              value={parseFloat(row.original.quantidade)}
              itemId={row.original.id}
              isReadOnly={isReadOnly}
              onUpdate={handleUpdateQuantity}
            />
          ),
        }
      );

      return baseColumns;
    },
    [hideLotsAndValidity, isReadOnly, handleUpdateQuantity, jobClientes]
  );

  const table = useMantineReactTable({
    columns,
    data: filteredCirurgias,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: isEditMode,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: {
      showColumnFilters: true,
      density: 'xs',
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': { padding: '8px 12px !important', fontSize: '0.75rem' },
        'td': { padding: '4px 12px !important', fontSize: '0.75rem' }
      }
    },
    renderRowActions: isEditMode ? ({ row }) => (
      <ActionIcon
        onClick={(e) => {
          e.stopPropagation();
          if (confirm(`Excluir cirurgia "${row.original.codigo_produto}" - Lote ${row.original.lote}?`)) {
            handleDeleteItem(row.original.id);
          }
        }}
        loading={deletingId === row.original.id}
        variant="subtle"
        color="red"
        size="sm"
        title={`Excluir cirurgia ${row.original.codigo_produto}`}
      >
        <Trash2 size={14} />
      </ActionIcon>
    ) : undefined,
    mantinePaperProps: { shadow: 'none', withBorder: true, radius: 'md' },
    localization: MRT_LOCALIZATION_PT_BR,
    mantineTableContainerProps: { style: { maxHeight: '600px' } },
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <RefreshCw className="animate-spin" size={20} />
          <span>Carregando cirurgias...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header com Botões */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          <PermissionGate tela="CONTAGENS_LIST" acao="exportar">
            <button
              onClick={handleDownloadExcel}
              className="flex items-center space-x-2 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition shadow-sm font-medium disabled:bg-gray-400"
              disabled={downloading || filteredCirurgias.length === 0}
            >
              <Download size={18} className={downloading ? 'animate-bounce' : ''} />
              <span>{downloading ? 'Baixando...' : 'Exportar Excel'}</span>
            </button>
          </PermissionGate>

          <PermissionGate tela="CONTAGENS_LIST" acao="criar">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center space-x-2 px-4 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition shadow-sm font-medium"
            >
              <Download size={18} className="rotate-180" />
              <span>Importar Excel</span>
            </button>
          </PermissionGate>

          <button
            onClick={() => setShowInfoModal(true)}
            className="p-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg transition"
            title="Informações sobre importação"
          >
            <Info size={18} />
          </button>

          <button
            onClick={loadCirurgias}
            className="p-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition"
            title="Atualizar lista"
          >
            <RefreshCw size={18} />
          </button>

          {/* Toggle Ocultar lotes e validade */}
          <div className="flex items-center ml-4 space-x-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg">
            <div
              className={`relative w-10 h-5 rounded-full cursor-pointer transition-colors duration-300 ${hideLotsAndValidity ? 'bg-green-600' : 'bg-gray-400'}`}
              onClick={() => setHideLotsAndValidity(!hideLotsAndValidity)}
            >
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-300 ${hideLotsAndValidity ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
            <span
              className="text-sm font-medium text-gray-700 dark:text-gray-300 select-none cursor-pointer"
              onClick={() => setHideLotsAndValidity(!hideLotsAndValidity)}
            >
              Ocultar lotes e validade
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {isEditMode && (
            <PermissionGate tela="CONTAGENS_LIST" acao="criar">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition shadow-sm font-medium"
              >
                <Plus size={18} />
                <span>Adicionar Cirurgia</span>
              </button>
            </PermissionGate>
          )}

          <div className="text-sm text-gray-500 dark:text-gray-400">
            <span className="font-semibold">{filteredCirurgias.length}</span> cirurgias
            {globalClienteFilter && globalClienteFilter.length > 0 && cirurgias.length !== filteredCirurgias.length && (
              <span className="ml-1 text-blue-500">(de {cirurgias.length} total)</span>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
          <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            ×
          </button>
        </div>
      )}

      {/* Epic 49: Legenda de cores de vencimento */}
      {!hideLotsAndValidity && filteredCirurgias.length > 0 && (
        <ValidadeLegend className="mb-2" />
      )}

      {/* Tabela com MantineReactTable */}
      {filteredCirurgias.length > 0 ? (
        <MantineReactTable table={table} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 p-8">
          <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
            <Stethoscope size={32} className="text-gray-400" />
            <span>Nenhuma cirurgia importada.</span>
            <span className="text-xs">Use "Importar Excel" ou "Adicionar Cirurgia" para adicionar itens.</span>
          </div>
        </div>
      )}

      {/* Summary Card */}
      {filteredCirurgias.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Cirurgias:</span>
              <span className="ml-2 font-bold text-green-700 dark:text-green-400">{filteredCirurgias.length}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Itens:</span>
              <span className="ml-2 font-bold text-green-700 dark:text-green-400">
                {filteredCirurgias.reduce((sum, item) => sum + parseFloat(item.quantidade), 0)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Clientes:</span>
              <span className="ml-2 font-bold text-green-700 dark:text-green-400">
                {new Set(filteredCirurgias.map(c => c.cliente_nome_fantasia).filter(Boolean)).size}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Produtos únicos:</span>
              <span className="ml-2 font-bold text-green-700 dark:text-green-400">
                {new Set(filteredCirurgias.map(c => c.codigo_produto)).size}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <ImportExcelCirurgiasModal
          error={error}
          selectedFile={selectedFile}
          uploading={uploading}
          uploadResult={uploadResult}
          jobClientes={jobClientes}
          selectedImportClient={selectedImportClient}
          selectedRegime={selectedRegime}
          setSelectedFile={setSelectedFile}
          setSelectedImportClient={setSelectedImportClient}
          setSelectedRegime={setSelectedRegime}
          handleUploadExcel={handleUploadExcel}
          closeImportModal={closeImportModal}
          setError={setError}
          importMode={importMode}
          setImportMode={setImportMode}
          pastedText={pastedText}
          setPastedText={setPastedText}
          pastedRows={pastedRows}
        />
      )}

      {/* Add Cirurgia Modal */}
      <AddCirurgiaModal
        isOpen={showAddModal}
        jobClientes={jobClientes}
        onSubmit={handleAddCirurgia}
        onClose={() => setShowAddModal(false)}
        loading={addingItem}
      />

      {/* Info Modal */}
      {showInfoModal && (
        <CirurgiasInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </div>
  );
};
